import { createSelector } from "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQuery } from "../../../shared/api/base-query";
import { filterNotDeleted } from "../../../shared/api/filter-not-deleted";
import { pagedQueryFn } from "../../../shared/api/paged-query-fn";
import type { QuerySubscriptionOptions } from "../../../shared/api/query-subscription-options";
import type { IFertilisersDto } from "../shared";
import { Fertiliser } from "../shared/models/fertiliser";

export const fertilisersApiSlice = createApi({
  reducerPath: "api-fertilisers",
  // https://github.com/reduxjs/redux-toolkit/discussions/2347#discussioncomment-2978038
  refetchOnFocus: false,
  keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
  baseQuery,
  tagTypes: ["FertilisersList", "FertilisersListCount"],
  endpoints: (builder) => ({
    fetchFertilisersWithDeleted: builder.query<Fertiliser[], void>({
      queryFn: pagedQueryFn<IFertilisersDto, Fertiliser[]>(
        { url: "FertilisersV2", params: { includeDeleted: true } },
        (dtos) => {
          const models: Fertiliser[] = dtos.map((dto) => {
            const model = new Fertiliser(dto.id);
            model.updateFromDto(dto);
            return model;
          });
          models.sort((a: Fertiliser, b: Fertiliser) => a.name.localeCompare(b.name));
          return models;
        }
      ),
    }),
  }),
});

const selectNotDeletedData = createSelector(
  ({ data }: { data?: Fertiliser[] }) => data,
  filterNotDeleted<Fertiliser>
);

const selectNotDeletedCurrentData = createSelector(
  ({ currentData }: { currentData?: Fertiliser[] }) => currentData,
  filterNotDeleted<Fertiliser>
);

const useFertilisersEndpoint =
  fertilisersApiSlice.endpoints.fetchFertilisersWithDeleted.useQuery;

type FertilisersQueryOptions = QuerySubscriptionOptions<typeof useFertilisersEndpoint>;

export function useFetchFertilisers(options?: FertilisersQueryOptions) {
  return useFertilisersEndpoint(undefined, {
    ...options,
    selectFromResult: (result) => ({
      ...result,
      data: selectNotDeletedData(result),
      currentData: selectNotDeletedCurrentData(result),
    }),
  });
}

export function useFetchFertilisersWithDeleted(options?: FertilisersQueryOptions) {
  return useFertilisersEndpoint(undefined, options);
}
